import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import getFormTitle from '#/common/utils/getFormTitle';
import useAssetPlanQuery from '../hooks/useAssetPlanQuery';
import useNotFoundGuard from '#/common/hooks/useNotFoundGuard';
import RecordNotFound from '#/app/pages/errors/RecordNotFound';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import If from '#/common/components/If';
import AssetPlanItemsDataGrid from '../components/AssetPlanItemsDataGrid';
import AssetPlanDashboard from '../components/AssetPlanDashboard';

export interface AssetPlanParams {
  readonly id?: string;
}

export type AssetPlanTabs = 'items' | 'dashboard';

export interface AssetPlanProps {
  readonly tab?: AssetPlanTabs;
}

const tabs: Tab<AssetPlanTabs>[] = [
  {
    key: 'items',
    text: 'Plan Items',
    type: 'grid',
  },
  {
    key: 'dashboard',
    text: 'Plan Dashboard',
    type: 'dashboard',
  },
];

const AssetPlanPage: React.FC<AssetPlanProps> = ({ tab }) => {
  const { id } = useParams() as AssetPlanParams;
  const assetPlanQuery = useAssetPlanQuery(id);
  const assetPlan = assetPlanQuery.data?.getAssetPlan?.data;
  const isNotFound = useNotFoundGuard({ loading: assetPlanQuery.loading, id, entity: assetPlan });
  const title = getFormTitle(id, assetPlan?.displayNumber ?? undefined, 'Asset Plan');
  const selectedTab = tab === 'items' ? 'items' : 'dashboard';

  const pageType = useMemo<'dashboard' | 'list'>(() => {
    if (selectedTab === 'dashboard') return 'dashboard';
    return 'list';
  }, [selectedTab]);

  if (isNotFound) {
    return (
      <SubPage title={title} showGoBackButton type="list" isLoading={false}>
        <RecordNotFound backUrl="/asset-plans" />
      </SubPage>
    );
  }

  return (
    <SubPage
      title={title || 'Asset Plan'}
      tabs={tabs}
      selectedTab={selectedTab}
      showGoBackButton
      type={pageType}
      hideSelectToggle
      isLoading={assetPlanQuery.loading}>
      <If condition={selectedTab === 'items'}>
        {id && (
          <AssetPlanItemsDataGrid
            activeScenarioId={assetPlan?.activeScenarioId ?? ''}
            startYear={assetPlan?.startYear ?? undefined}
            endYear={assetPlan?.endYear ?? undefined}
          />
        )}
      </If>
      <If condition={selectedTab === 'dashboard'}>
        {id && assetPlan?.activeScenarioId && (
          <AssetPlanDashboard
            assetPlanId={id}
            scenarioId={assetPlan.activeScenarioId}
            assetPlan={assetPlan}
          />
        )}
      </If>
    </SubPage>
  );
};

export default React.memo(AssetPlanPage);
